import {
  adjustAdminBundleForCumulativeHov,
  type HoverIdleAdminStateBundle,
} from "@/features/admin/adminState";
import type { AchievementsModel } from "@/features/achievements/achievementsModel";
import type { HoverIdleModel } from "@/features/hoverIdle/core/hoverIdleModel";
import type { HovSkillModel } from "@/features/hoverIdle/hov/hovSkillModel";
import type { TimSkillModel } from "@/features/hoverIdle/tim/timSkillModel";

export interface ApplyAdminStateBundleToModelsOptions {
  readonly model: HoverIdleModel;
  readonly achievements: AchievementsModel;
  readonly hovSkillModel: HovSkillModel;
  readonly timSkillModel: TimSkillModel;
  readonly incomingBundle: HoverIdleAdminStateBundle;
  readonly restoreMode?: "edit" | "checkpoint";
  readonly syncAchievementPersistentEffects: () => void;
  readonly syncTimHovActivationUnlock: () => void;
}

export function applyAdminStateBundleToModels(
  options: ApplyAdminStateBundleToModelsOptions,
): HoverIdleAdminStateBundle {
  const previousModelState = options.model.exportState();
  const bundle =
    options.restoreMode === "checkpoint"
      ? options.incomingBundle
      : adjustAdminBundleForCumulativeHov(options.incomingBundle, {
          hov: previousModelState.economy.hov,
          totalHovEarned: previousModelState.cumulativeStats.totalHovEarned,
          currentLoopHovEarned:
            previousModelState.economy.mHov.currentLoopHovEarned,
        });

  options.achievements.restoreState(bundle.achievementsState);
  if (options.restoreMode === "checkpoint") {
    options.hovSkillModel.restoreState(bundle.hovSkillState);
    options.timSkillModel.restoreState(bundle.timSkillState);
    // Finalize incoming skill, purchase, and Wall sources before restoring the
    // saved assignments again; provisional old capacity may trim this pass.
    options.model.restoreState(bundle.modelState);
  }
  options.syncAchievementPersistentEffects();
  options.syncTimHovActivationUnlock();

  options.model.restoreState(bundle.modelState);
  options.hovSkillModel.restoreState(bundle.hovSkillState);
  options.timSkillModel.restoreState(bundle.timSkillState);
  // `restoreState` resets effect-source intents; reapply restored achievement
  // effects before later Hov skill syncing composes MPM unlocks again.
  options.syncAchievementPersistentEffects();
  options.syncTimHovActivationUnlock();

  return bundle;
}
